import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, EmptyValue, Modal, RowActions, SegmentedControl, Spinner, Table } from '@/components/ui';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import ServerPagination from '../../../components/ServerPagination';
import { Vehicle, VehiclePmInspection, VehiclePmInspectionItem } from '../../../types/Vehicle';
import vehicleService from '../../../services/vehicleService';
import { getPmTypes, type PmTypeOption } from '../../../services/equipmentPmService';
import { formatDate } from '../../../utils/wrapDate';
import { odometerUnitOf, printPmInspections } from '../utils/equipmentPrint';
import EquipmentDateRangeModal, { REPORT_PRESET_MONTHS } from './EquipmentDateRangeModal';

interface EquipmentPmHistoryTabProps {
  vehicle: Vehicle;
}

function resultBg(value: string): 'success' | 'danger' | 'secondary' {
  const v = value.trim().toLowerCase();
  if (v === 'pass') return 'success';
  if (v === 'fail') return 'danger';
  return 'secondary';
}

/** Result | item | measurement | comments; result + item only on phones, the rest wraps under the item. */
const ITEM_GRID = 'tw-grid-cols-[4rem_minmax(0,1fr)] md:tw-grid-cols-[4.5rem_minmax(0,1fr)_9rem_minmax(0,18rem)]';

function fmtOdometer(value: number | null | undefined, unit: string): React.ReactNode {
  return value == null ? <EmptyValue /> : `${value.toLocaleString('en-CA')} ${unit}`;
}

function fmtDate(value?: string | null): React.ReactNode {
  return formatDate(value ?? null) ?? <EmptyValue />;
}

/** Items grouped by inspection type, keeping the order they were recorded in. */
function groupItems(items: VehiclePmInspectionItem[]): { type: string; items: VehiclePmInspectionItem[] }[] {
  const groups: { type: string; items: VehiclePmInspectionItem[] }[] = [];
  items.forEach((item) => {
    const last = groups[groups.length - 1];
    if (last && last.type === item.inspectionType) last.items.push(item);
    else groups.push({ type: item.inspectionType, items: [item] });
  });
  return groups;
}

const EquipmentPmHistoryTab: React.FC<EquipmentPmHistoryTabProps> = ({ vehicle }) => {
  const [rows, setRows] = useState<VehiclePmInspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<VehiclePmInspection | null>(null);
  const [detail, setDetail] = useState<VehiclePmInspection | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [failsOnly, setFailsOnly] = useState(false);
  const [showPrintAll, setShowPrintAll] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  const [pmTypes, setPmTypes] = useState<string[]>([]);
  const [unitPmTypes, setUnitPmTypes] = useState<PmTypeOption[] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    vehicleService.getPmInspections(vehicle.id)
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load PM history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [vehicle.id]);

  // The unit's standard PM types (EQ-18). Falls back to the recorded values if the list can't load.
  useEffect(() => {
    let cancelled = false;
    getPmTypes(vehicle.id)
      .then((types) => {
        if (!cancelled) setUnitPmTypes(types);
      })
      .catch(() => {
        if (!cancelled) setUnitPmTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [vehicle.id]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    setFailsOnly(false);
    vehicleService.getPmInspection(vehicle.id, selected.id)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) setDetailError(err instanceof Error ? err.message : 'Failed to load PM');
      });
    return () => {
      cancelled = true;
    };
  }, [selected, vehicle.id]);

  const unit = odometerUnitOf(vehicle);

  const pmTypeLabels = useMemo(
    () => Object.fromEntries((unitPmTypes || []).map((type) => [type.code, type.label])) as Record<string, string>,
    [unitPmTypes]
  );
  const typeLabel = (code: string) => pmTypeLabels[code] ?? code;

  // The unit's PM types plus Grease, in the standard order; recorded values only as a fallback.
  const pmTypeOptions = useMemo<PmTypeOption[]>(() => {
    if (unitPmTypes && unitPmTypes.length > 0) return unitPmTypes;
    return Array.from(new Set(rows.map((row) => row.pmType).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map((code) => ({ code, label: code }));
  }, [unitPmTypes, rows]);

  const filteredRows = useMemo(
    () => (pmTypes.length === 0 ? rows : rows.filter((row) => pmTypes.includes(row.pmType))),
    [rows, pmTypes]
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pageRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  const detailItems = useMemo(() => {
    const items = detail?.items || [];
    return failsOnly ? items.filter((item) => resultBg(item.inspectionValue) !== 'success') : items;
  }, [detail, failsOnly]);

  const tally = useMemo(() => {
    const items = detail?.items || [];
    const pass = items.filter((item) => resultBg(item.inspectionValue) === 'success').length;
    const fail = items.filter((item) => resultBg(item.inspectionValue) === 'danger').length;
    return { pass, fail, other: items.length - pass - fail };
  }, [detail]);

  const failedItems = useMemo(
    () => (detail?.items || []).filter((item) => resultBg(item.inspectionValue) === 'danger'),
    [detail]
  );

  const printOne = async () => {
    if (!detail) return;
    setDetailError(null);
    try {
      await printPmInspections({ vehicle, pmTypeLabels, load: async () => [detail] });
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : 'Failed to print PM');
    }
  };

  if (loading) {
    return (
      <div className="tw-py-6 tw-text-center">
        <Spinner role="status"><span className="tw-sr-only">Loading...</span></Spinner>
      </div>
    );
  }

  if (error) {
    return <p className="tw-text-sm tw-text-danger">{error}</p>;
  }

  return (
    <>
      <Card>
        <Card.Header className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <h2 className="tw-mb-0 tw-text-base tw-font-semibold">PM History</h2>
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={rows.length === 0}
            onClick={() => {
              setPrintError(null);
              setShowPrintAll(true);
            }}
          >
            <i className="bi bi-printer tw-mr-1" aria-hidden="true" />
            Print All PMs
          </Button>
        </Card.Header>
        <Card.Body className="tw-p-0">
          {printError && <p className="tw-mb-0 tw-px-4 tw-pt-3 tw-text-sm tw-text-danger">{printError}</p>}
          {rows.length > 0 && (
            <div className="tw-px-4 tw-pt-3 md:tw-max-w-sm">
              <MultiSelectDropdown
                label="PM Type"
                options={pmTypeOptions}
                value={pmTypes}
                onChange={(next) => {
                  setPmTypes(next.map(String));
                  setPage(1);
                }}
                getValue={(type) => type.code}
                formatOption={(type) => type.label}
                preserveOptionsOrder
                placeholder="All PM types"
                showSearch={pmTypeOptions.length > 6}
              />
            </div>
          )}
          {rows.length === 0 ? (
            <p className="tw-mb-0 tw-p-4 tw-text-sm tw-text-content-muted">No PMs have been recorded on this unit.</p>
          ) : filteredRows.length === 0 ? (
            <p className="tw-mb-0 tw-p-4 tw-text-sm tw-text-content-muted">No PMs match the selected types.</p>
          ) : (
            <Table responsive hover className="tw-mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>PM Type</th>
                  <th>Employee</th>
                  <th className="tw-text-right">Odometer</th>
                  <th>Result</th>
                  <th className="tw-text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr
                    key={row.id}
                    className="tw-cursor-pointer"
                    onClick={() => setSelected(row)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setSelected(row);
                    }}
                  >
                    <td className="tw-whitespace-nowrap">{fmtDate(row.performedAt)}</td>
                    <td>{row.pmType ? typeLabel(row.pmType) : <EmptyValue />}</td>
                    <td>{row.employeeName || <EmptyValue />}</td>
                    <td className="tw-whitespace-nowrap tw-text-right">{fmtOdometer(row.odometer, unit)}</td>
                    <td>
                      <div className="tw-flex tw-flex-wrap tw-gap-1">
                        <Badge bg="secondary">{row.itemCount ?? 0} items</Badge>
                        {(row.failCount ?? 0) > 0 ? (
                          <Badge bg="danger">{row.failCount} fail</Badge>
                        ) : (
                          <Badge bg="success">All pass</Badge>
                        )}
                      </div>
                    </td>
                    <td>
                      <RowActions actions={[{ kind: 'view', label: 'View PM', onClick: () => setSelected(row) }]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <ServerPagination
            className="tw-px-4 tw-pb-3"
            currentPage={page}
            totalPages={totalPages}
            totalCount={filteredRows.length}
            pageSize={pageSize}
            pageSizeOptions={[10, 25, 50]}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </Card.Body>
      </Card>

      <Modal show={selected != null} onHide={() => setSelected(null)} size="2xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {selected ? `${typeLabel(selected.pmType)} PM · ${formatDate(selected.performedAt) ?? ''}`.trim() : 'View PM'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailError && <p className="tw-mb-2 tw-text-sm tw-text-danger">{detailError}</p>}
          {selected && (
            <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-stretch">
              <dl className="tw-mb-0 tw-grid tw-flex-1 tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3 tw-rounded-lg tw-border tw-border-line tw-bg-surface-alt tw-p-3 sm:tw-grid-cols-5">
                {[
                  ['Unit', vehicle.unitNumber || <EmptyValue />],
                  ['Date', fmtDate(selected.performedAt)],
                  ['PM Type', selected.pmType ? typeLabel(selected.pmType) : <EmptyValue />],
                  ['Employee', selected.employeeName || <EmptyValue />],
                  ['Odometer', fmtOdometer(selected.odometer, unit)]
                ].map(([label, value]) => (
                  <div key={String(label)}>
                    <dt className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted">{label}</dt>
                    <dd className="tw-mb-0 tw-text-sm tw-font-medium tw-text-content">{value}</dd>
                  </div>
                ))}
              </dl>
              {detail && (
                <div className="tw-grid tw-grid-cols-3 tw-gap-2 lg:tw-w-72">
                  {[
                    { label: 'Items', value: detail.items?.length ?? 0, cls: 'tw-text-content' },
                    { label: 'Pass', value: tally.pass, cls: 'tw-text-success' },
                    { label: 'Fail', value: tally.fail, cls: tally.fail > 0 ? 'tw-text-danger' : 'tw-text-content-muted' }
                  ].map((stat) => (
                    <div key={stat.label} className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-line tw-p-2">
                      <span className={`tw-text-2xl tw-font-bold tw-leading-none ${stat.cls}`}>{stat.value}</span>
                      <span className="tw-mt-1 tw-text-xs tw-uppercase tw-tracking-wide tw-text-content-muted">{stat.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {!detail && !detailError ? (
            <div className="tw-py-6 tw-text-center">
              <Spinner role="status"><span className="tw-sr-only">Loading...</span></Spinner>
            </div>
          ) : detail ? (
            <>
              {failedItems.length > 0 && (
                <div className="tw-mb-4 tw-rounded-lg tw-border tw-border-danger/40 tw-border-l-4 tw-border-l-danger tw-bg-danger/5 tw-p-3">
                  <h3 className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-danger">
                    <i className="bi bi-exclamation-triangle tw-mr-1" aria-hidden="true" />
                    Items Requiring Attention
                  </h3>
                  <ul className="tw-mb-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0 tw-text-sm">
                    {failedItems.map((item) => (
                      <li key={item.id}>
                        <span className="tw-font-semibold">{item.inspectionType}</span> – {item.inspectionItem}
                        {item.comments && (
                          <span className="tw-block tw-italic tw-text-content-secondary">{item.comments}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                <h3 className="tw-mb-0 tw-text-base tw-font-semibold">Inspection Items</h3>
                <SegmentedControl
                  aria-label="Which items to show"
                  value={failsOnly ? 'fails' : 'all'}
                  onChange={(value) => setFailsOnly(value === 'fails')}
                  options={[
                    { value: 'all', label: `All (${detail.items?.length ?? 0})` },
                    { value: 'fails', label: `Fails (${tally.fail + tally.other})` }
                  ]}
                />
              </div>
              {detailItems.length === 0 ? (
                <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
                  {failsOnly ? 'Every item passed.' : 'No inspection items were recorded on this PM.'}
                </p>
              ) : (
                <div className="tw-flex tw-flex-col tw-gap-4">
                  {groupItems(detailItems).map((group) => {
                    const groupFails = group.items.filter((item) => resultBg(item.inspectionValue) === 'danger').length;
                    return (
                      <section key={group.type} className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-line">
                        <header className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-bg-surface-alt tw-px-3 tw-py-2">
                          <h4 className="tw-mb-0 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content">
                            {group.type}
                          </h4>
                          <span className="tw-text-xs tw-text-content-muted">
                            {group.items.length} {group.items.length === 1 ? 'item' : 'items'}
                            {groupFails > 0 && <span className="tw-ml-1 tw-font-semibold tw-text-danger">· {groupFails} fail</span>}
                          </span>
                        </header>
                        <div className={`${ITEM_GRID} tw-hidden tw-border-b tw-border-line tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted md:tw-grid`}>
                          <span>Result</span>
                          <span>Inspection Item</span>
                          <span>Measurement</span>
                          <span>Comments</span>
                        </div>
                        <ul className="tw-mb-0 tw-list-none tw-divide-y tw-divide-line tw-p-0">
                          {group.items.map((item) => {
                            const failed = resultBg(item.inspectionValue) === 'danger';
                            const measured = [item.measurementValue, item.unitOfMeasurement].filter(Boolean).join(' ');
                            return (
                              <li
                                key={item.id}
                                className={`${ITEM_GRID} tw-grid tw-items-start tw-gap-x-3 tw-gap-y-1 tw-px-3 tw-py-2 tw-text-sm ${failed ? 'tw-bg-danger/5' : ''}`}
                              >
                                <span>
                                  <Badge bg={resultBg(item.inspectionValue)}>{item.inspectionValue}</Badge>
                                </span>
                                <span className={`tw-min-w-0 tw-break-words ${failed ? 'tw-font-medium' : ''}`}>{item.inspectionItem}</span>
                                <span className="tw-col-start-2 tw-min-w-0 md:tw-col-start-auto">
                                  {measured ? (
                                    <span className="tw-font-semibold tw-text-primary">{measured}</span>
                                  ) : (
                                    <EmptyValue className="tw-hidden md:tw-inline" />
                                  )}
                                </span>
                                <span className="tw-col-start-2 tw-min-w-0 tw-whitespace-pre-wrap md:tw-col-start-auto tw-break-words tw-text-content-secondary">
                                  {item.comments ? (
                                    <span className="tw-italic">{item.comments}</span>
                                  ) : (
                                    <EmptyValue className="tw-hidden md:tw-inline" />
                                  )}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </section>
                    );
                  })}
                </div>
              )}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>
          <Button onClick={printOne} disabled={!detail}>
            <i className="bi bi-printer tw-mr-1" aria-hidden="true" />
            Print PM
          </Button>
        </Modal.Footer>
      </Modal>

      <EquipmentDateRangeModal
        show={showPrintAll}
        title="Print All PMs"
        description="Prints every PM completed in the period, one PM per page."
        confirmLabel="Print"
        presetMonths={REPORT_PRESET_MONTHS}
        defaultPresetMonths={24}
        onHide={() => setShowPrintAll(false)}
        onConfirm={({ from, to }) =>
          printPmInspections({
            vehicle,
            pmTypeLabels,
            emptyMessage:
              pmTypes.length > 0
                ? 'No PMs of the selected types were completed on this unit in the selected period.'
                : 'No PMs were completed on this unit in the selected period.',
            load: async () => {
              const all = await vehicleService.getPmInspections(vehicle.id, {
                from: from.toISOString(),
                to: to.toISOString(),
                includeItems: true
              });
              return pmTypes.length === 0 ? all : all.filter((pm) => pmTypes.includes(pm.pmType));
            }
          })
        }
      >
        <p className="tw-mb-0 tw-mt-3 tw-text-sm tw-text-content-secondary">
          PM Types: {pmTypes.length === 0 ? 'All' : pmTypes.map(typeLabel).join(', ')}
        </p>
      </EquipmentDateRangeModal>
    </>
  );
};

export default EquipmentPmHistoryTab;
